import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { MetricCard } from '@/components/MetricCard';
import { BarChart, ChartContainer } from '@/components/charts';
import { useAlertSlaCompliance, useAlertSendoutsByMethod } from '../hooks';
import type { PeriodFilter } from '../api';
import {
  RefreshCw, Bell, Send, CheckCircle, Clock, TrendingUp, Users,
  ArrowLeft, Target
} from 'lucide-react';
import { cn } from '@/lib/utils';

type Period = PeriodFilter;

export default function AlertSendoutsPage() {
  const { t } = useTranslation('intelligence');
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('week');

  const { data: sla, isLoading: slaLoading, refetch, isFetching } = useAlertSlaCompliance(period);
  const { data: methods, isLoading: methodsLoading } = useAlertSendoutsByMethod(period);

  const periods: { value: Period; label: string }[] = [
    { value: 'day', label: t('communications.alerts.periods.day', 'Today') },
    { value: 'week', label: t('communications.alerts.periods.week', 'This Week') },
    { value: 'month', label: t('communications.alerts.periods.month', 'This Month') },
    { value: 'quarter', label: t('communications.alerts.periods.quarter', 'This Quarter') },
  ];

  const formatNumber = (num: number | undefined) => {
    if (num === undefined || num === null) return '--';
    return num.toLocaleString();
  };

  const formatPercent = (value: number | undefined) => {
    if (value === undefined || value === null) return '--';
    return `${(Math.round(value * 10) / 10)}%`;
  };

  const formatMinutes = (minutes: number | undefined) => {
    if (minutes === undefined || minutes === null) return '--';
    if (minutes < 1) return `${Math.round(minutes * 60)}s`;
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const getMethodLabel = (method: string) => {
    const labels: Record<string, string> = {
      email: t('communications.alerts.methods.email', 'Email'),
      sms: t('communications.alerts.methods.sms', 'SMS'),
      phone: t('communications.alerts.methods.phone', 'Phone'),
      alphapager: t('communications.alerts.methods.alphapager', 'Alpha Pager'),
      numericpager: t('communications.alerts.methods.numericpager', 'Numeric Pager'),
    };
    return labels[method] || method;
  };

  const goBack = () => {
    navigate('/intelligence/communications');
  };

  // SLA breakdown chart data
  const slaChartData = [
    { name: t('communications.alerts.sla.under1min', '< 1 min'), value: sla?.under_1min || 0, color: '#27ae60' },
    { name: t('communications.alerts.sla.between1And5min', '1-5 min'), value: sla?.between_1_5min || 0, color: '#2ecc71' },
    { name: t('communications.alerts.sla.between5And15min', '5-15 min'), value: sla?.between_5_15min || 0, color: '#f39c12' },
    { name: t('communications.alerts.sla.between15And60min', '15-60 min'), value: sla?.between_15_60min || 0, color: '#e67e22' },
    { name: t('communications.alerts.sla.over60min', '> 60 min'), value: sla?.over_60min || 0, color: '#e74c3c' },
  ].filter(d => d.value > 0);

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={goBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('communications.alerts.backToOverview', 'Back to Overview')}
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Bell className="h-6 w-6 text-green-600" />
              {t('communications.alerts.title', 'Alert Sendouts Analytics')}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-lg border bg-white overflow-hidden">
            {periods.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium transition-colors',
                  period === p.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            {t('common:refresh', 'Refresh')}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <MetricCard
          title={t('communications.alerts.cards.totalSendouts', 'Total Sendouts')}
          value={sla?.total_sendouts}
          icon={<Send className="h-5 w-5" />}
          color="green"
          loading={slaLoading}
        />
        <MetricCard
          title={t('communications.alerts.cards.acknowledged', 'Acknowledged')}
          value={sla?.total_acknowledged}
          icon={<CheckCircle className="h-5 w-5" />}
          color="blue"
          loading={slaLoading}
        />
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('communications.alerts.cards.slaCompliance', 'SLA Compliance')}</p>
                {slaLoading ? (
                  <Skeleton className="h-6 w-16 mt-1" />
                ) : (
                  <p className="text-xl font-bold text-green-600">
                    {formatPercent(sla?.sla_compliance_15min_pct)}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('communications.alerts.cards.avgResponseTime', 'Avg. Response Time')}</p>
                {slaLoading ? (
                  <Skeleton className="h-6 w-16 mt-1" />
                ) : (
                  <p className="text-xl font-bold text-blue-600">
                    {formatMinutes(sla?.avg_response_time_minutes)}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white col-span-2">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Target className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('communications.alerts.cards.ackRate', 'Acknowledgment Rate')}</p>
                {slaLoading ? (
                  <Skeleton className="h-6 w-16 mt-1" />
                ) : (
                  <p className="text-xl font-bold text-purple-600">
                    {sla?.total_sendouts && sla?.total_acknowledged !== undefined
                      ? formatPercent((sla.total_acknowledged / sla.total_sendouts) * 100)
                      : '--'}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* SLA Breakdown + Methods */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* SLA Breakdown Chart */}
        <Card>
          <CardHeader>
            <CardTitle>{t('communications.alerts.charts.slaBreakdown', 'Response Time Distribution')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer loading={slaLoading} height={300}>
              {slaChartData.length > 0 ? (
                <BarChart
                  data={slaChartData}
                  height={270}
                  colors={['#27ae60', '#2ecc71', '#f39c12', '#e67e22', '#e74c3c']}
                  colorByValue
                />
              ) : (
                <div className="flex items-center justify-center h-[270px] text-gray-400">
                  <div className="text-center">
                    <Bell className="h-12 w-12 mx-auto mb-2" />
                    <p>{t('communications.alerts.noData', 'No SLA data available')}</p>
                  </div>
                </div>
              )}
            </ChartContainer>
          </CardContent>
        </Card>

        {/* By Method Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t('communications.alerts.sections.methods', 'By Delivery Method')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('communications.alerts.table.method', 'Method')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('communications.alerts.table.sent', 'Sent')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('communications.alerts.table.delivered', 'Delivered')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('communications.alerts.table.acknowledged', 'Acknowledged')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('communications.alerts.table.ackRate', 'Ack Rate')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {methodsLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 5 }).map((_, j) => (
                          <td key={j} className="px-4 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : methods && methods.length > 0 ? (
                    methods.map((method, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {getMethodLabel(method.recipient_method)}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          {formatNumber(method.sent)}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          {formatNumber(method.delivered)}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          {formatNumber(method.acknowledged)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={cn(
                            'text-xs',
                            (method.ack_rate || 0) >= 80 ? 'bg-green-100 text-green-800' :
                            (method.ack_rate || 0) >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                          )}>
                            {formatPercent(method.ack_rate)}
                          </Badge>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                        {t('communications.alerts.noMethods', 'No method data available')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Response Time Legend */}
      <Card>
        <CardHeader>
          <CardTitle>{t('communications.alerts.sections.sla', 'SLA Compliance Legend')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
              <div className="w-4 h-4 rounded bg-green-500"></div>
              <div>
                <p className="text-sm font-medium text-green-800">{t('communications.alerts.sla.under1min', '< 1 min')}</p>
                {slaLoading ? (
                  <Skeleton className="h-5 w-12 mt-1" />
                ) : (
                  <p className="text-lg font-bold text-green-600">{formatNumber(sla?.under_1min)}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
              <div className="w-4 h-4 rounded bg-emerald-400"></div>
              <div>
                <p className="text-sm font-medium text-emerald-800">{t('communications.alerts.sla.between1And5min', '1-5 min')}</p>
                {slaLoading ? (
                  <Skeleton className="h-5 w-12 mt-1" />
                ) : (
                  <p className="text-lg font-bold text-emerald-600">{formatNumber(sla?.between_1_5min)}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
              <div className="w-4 h-4 rounded bg-yellow-500"></div>
              <div>
                <p className="text-sm font-medium text-yellow-800">{t('communications.alerts.sla.between5And15min', '5-15 min')}</p>
                {slaLoading ? (
                  <Skeleton className="h-5 w-12 mt-1" />
                ) : (
                  <p className="text-lg font-bold text-yellow-600">{formatNumber(sla?.between_5_15min)}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
              <div className="w-4 h-4 rounded bg-orange-500"></div>
              <div>
                <p className="text-sm font-medium text-orange-800">{t('communications.alerts.sla.between15And60min', '15-60 min')}</p>
                {slaLoading ? (
                  <Skeleton className="h-5 w-12 mt-1" />
                ) : (
                  <p className="text-lg font-bold text-orange-600">{formatNumber(sla?.between_15_60min)}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
              <div className="w-4 h-4 rounded bg-red-500"></div>
              <div>
                <p className="text-sm font-medium text-red-800">{t('communications.alerts.sla.over60min', '> 60 min')}</p>
                {slaLoading ? (
                  <Skeleton className="h-5 w-12 mt-1" />
                ) : (
                  <p className="text-lg font-bold text-red-600">{formatNumber(sla?.over_60min)}</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
